// Per-route document titles (WCAG 2.4.2 Page Titled). Every route used to share
// index.html's static <title>, so screen-reader users, browser history and tabs
// couldn't tell pages apart. RouteTracker sets the title on each navigation.

export const APP_TITLE = 'Reality Coach';

// Segments whose Title-Case default doesn't match the name the sidebar / page
// shows. Anything not listed falls back to kebab-case → Title Case.
const SEGMENT_LABELS: Record<string, string> = {
  roleplay: 'Role Play',
  'role-plays': 'Role Plays',
  sessions: 'Feedback',
  feedback: 'App Feedback',
  'reviewer-dashboard': 'My Reviews',
  'platform-dashboard': 'Platform Overview',
  login: 'Sign In',
  signup: 'Sign Up',
  sso: 'Single Sign-On',
  'marketplace-signup': 'Marketplace Sign Up',
  'payment-success': 'Payment Successful',
  new: 'New',
  edit: 'Edit',
};

const ERROR_LABELS: Record<string, string> = {
  '404': 'Page Not Found',
  '500': 'Server Error',
  '503': 'Service Unavailable',
};

// UUIDs and numeric ids name a record, not a page.
const ID_PATTERN = /^([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}|\d+)$/i;

const toLabel = (segment: string): string =>
  SEGMENT_LABELS[segment] ??
  segment
    .split('-')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');

/**
 * "/" → "Home | Reality Coach"; "/assignments/new" → "New · Assignments | Reality Coach";
 * "/sessions/<uuid>" → "Details · Feedback | Reality Coach". Most specific part first, so
 * the distinguishing word survives a truncated browser tab.
 */
export function getPageTitle(pathname: string): string {
  const parts = pathname.split('/').filter(Boolean);
  if (parts.length === 0) return `Home | ${APP_TITLE}`;
  if (parts[0] === 'error') return `${ERROR_LABELS[parts[1]] ?? 'Error'} | ${APP_TITLE}`;
  // /auth/magic/<token> — the token is neither an id nor a readable word.
  if (parts[0] === 'auth') return `Signing In | ${APP_TITLE}`;

  const labels: string[] = [];
  parts.forEach((part, index) => {
    if (!ID_PATTERN.test(part)) {
      labels.push(toLabel(part));
    } else if (index === parts.length - 1) {
      labels.push('Details');
    }
  });
  return `${labels.reverse().join(' · ')} | ${APP_TITLE}`;
}
